import React, { useEffect, useState } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";

// Components
import Navbar from "./components/Navbar";
import HomePage from "./components/HomePage";
import CeramicCoatingPage from "./components/CeramicCoatingPage";
import DealershipFleetPage from "./components/DealershipFleetPage";
import GiftCardsPage from "./components/GiftCardsPage";
import GiftCardSuccessPage from "./components/GiftCardSuccessPage"; 
import BookingSuccessPage from "./components/booking/BookingSuccessPage"; // Import booking success page


import BookingPage from "./components/booking/index";
import AccountPage from "./components/account/AccountPage";
import LoginPage from "./components/login/LoginPage";
import RegisterPage from "./components/login/RegisterPage"; 
import InteriorDetailingPage from "./components/InteriorDetailingPage";
import ExteriorDetailingPage from "./components/ExteriorDetailingPage";
import EstimatePage from "./components/EstimatePage";
import DesktopHomePage from "./components/DesktopHomePage";
import MobileHomePage from "./components/MobileHomePage";
import Footer from "./components/Footer";
import './firebase';
import ServicesPage from "./components/ServicesPage";
import ContactPage from "./components/ContactPage";

// Import Business Quote Components
import BusinessQuotePage from "./components/BusinessQuotePage";
import FleetQuotePage from "./components/FleetQuotePage";
import DealershipQuotePage from "./components/DealershipQuotePage";

// Import Admin Components
import AdminPage from "./components/admin/AdminPage";
import AdminProtectedRoute from "./components/admin/AdminProtectedRoute";

// Import admin setup utilities (for development)
import './utils/directElevation';

// Import CSS
import "./App.css";

import useWindowSize from "./hooks/useWindowSize";

// Protected route component
const ProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const auth = getAuth();
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthenticated(!!user);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [auth]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3A98B9] mx-auto"></div>
          <p className="mt-3 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  const locationHook = useLocation();
  const { width } = useWindowSize();
  const mobileBreakpoint = 768; // Define your mobile breakpoint
  
  // Determine if we're on mobile
  const isMobile = width < mobileBreakpoint;
  
  // Add mobile class to body
  useEffect(() => {
    if (isMobile) {
      document.body.classList.add('mobile-device');
    } else {
      document.body.classList.remove('mobile-device');
    }
  }, [isMobile]);
  
  // Animated scroll to top when route changes
  useEffect(() => {
    // Smooth scroll to top with animation
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [locationHook.pathname]);
  
  return (
    <div className={`min-h-screen flex flex-col ${locationHook.pathname !== '/' ? "page-with-navbar bg-white" : ""}`}>
      <Navbar isMobile={isMobile} />
      
      <main className="flex-grow">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={isMobile ? <MobileHomePage /> : <DesktopHomePage />} />
          <Route path="/ceramic-coating" element={<CeramicCoatingPage isMobile={isMobile} />} />
          <Route path="/dealership-fleet" element={<DealershipFleetPage isMobile={isMobile} />} />
          <Route path="/gift-cards" element={<GiftCardsPage isMobile={isMobile} />} />
          <Route path="/gift-card-success" element={<GiftCardSuccessPage isMobile={isMobile} />} />
          <Route path="/booking-success" element={<BookingSuccessPage />} /> {/* Add booking success route */}
          <Route path="/interior-detailing" element={<InteriorDetailingPage isMobile={isMobile} />} />
          <Route path="/exterior-detailing" element={<ExteriorDetailingPage isMobile={isMobile} />} />
          <Route path="/estimate" element={<EstimatePage isMobile={isMobile} />} />
          <Route path="/login" element={<LoginPage isMobile={isMobile} />} />
          <Route path="/register" element={<RegisterPage isMobile={isMobile} />} />
          <Route path="/services" element={<ServicesPage isMobile={isMobile} />} />
          
          {/* Business Quote Routes */}
          <Route path="/business-quote" element={<BusinessQuotePage isMobile={isMobile} />} />
          <Route path="/fleet-quote" element={<FleetQuotePage isMobile={isMobile} />} />
          <Route path="/dealership-quote" element={<DealershipQuotePage isMobile={isMobile} />} />
          <Route path="/contact" element={<ContactPage isMobile={isMobile} />} />

          {/* Routes that could be public or require authentication depending on your business logic */}
          <Route path="/book" element={<BookingPage isMobile={isMobile} />} />
          
          {/* Protected routes */}
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <AccountPage isMobile={isMobile} />
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <AdminProtectedRoute>
                <AdminPage isMobile={isMobile} />
              </AdminProtectedRoute>
            }
          />
          
          {/* Catch all / 404 */}
          <Route path="*" element={
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
                <p className="text-gray-600 mb-6">The page you're looking for doesn't exist.</p>
                <button 
                  onClick={() => window.history.back()} 
                  className="bg-[#3A98B9] text-white px-6 py-2 rounded-lg hover:bg-[#2A87A8]"
                >
                  Go Back
                </button>
              </div>
            </div>
          } />
        </Routes>
      </main>
      
      <Footer isMobile={isMobile} />
    </div>
  );
}

export default App;